import { getAccessToken, getUserPreferences, putUserPreferences, } from '@/lib/api';
import { PRIMARY_VIEWER_WINDOW_NAME, viewerWindowNameForSpan, } from '@/lib/hangingProtocols/monitorSpans';
import { isAppleTouchDevice } from '@/lib/platform';
import { DEFAULT_MONITOR_ROLES, DEFAULT_MULTI_SCREEN_SETTINGS } from '@/types/multiScreenSettings';
import i18n from 'i18next';
import { windowChannel } from './windowChannel';
import { discoverWindow } from './windowRoles';
const STORAGE_KEY = 'multi_screen_settings';
const SYNC_DEBOUNCE_MS = 1000;
class MultiScreenService {
    screenDetails = null;
    settings;
    listeners = new Set();
    /**
     * Handles to the popout windows this window opened. Used only to re-focus
     * and to know whether a window was closed (so it can be reopened) — actual
     * cross-window messaging goes through the BroadcastChannel, which also
     * covers windows opened by a previous page of this session.
     */
    viewerWindowHandle = null;
    reportWindowHandle = null;
    worklistWindowHandle = null;
    syncTimer = null;
    syncInitialized = false;
    /**
     * Scalar prefs (timezone, locale, ...) as last read from the server, round-tripped
     * on PUT so they survive. Null until a GET succeeds: a PUT must never carry
     * made-up defaults, or opening a second monitor would silently reset a German
     * user's locale to en-US. The server keeps omitted scalars as they are.
     */
    prefsBase = null;
    constructor() {
        this.settings = this.loadSettings();
    }
    /**
     * Pull the device-agnostic toggles (enabled, openFullscreen) from
     * /users/me/preferences and overlay them on top of localStorage. Raw
     * monitor configs / screen indices stay device-local — they describe the
     * physical workstation, not the user.
     */
    async initFromBackend() {
        if (this.syncInitialized)
            return;
        if (!getAccessToken())
            return;
        try {
            const remote = await getUserPreferences();
            this.prefsBase = {
                timezone: remote.timezone,
                timeFormat: remote.timeFormat,
                locale: remote.locale,
                sidebarClinicDisplayName: remote.sidebarClinicDisplayName,
            };
            this.settings = {
                ...this.settings,
                enabled: remote.multiScreen.enabled,
                openFullscreen: remote.multiScreen.openFullscreen,
            };
            this.saveSettingsToLocalStorage();
            this.notifyListeners();
        }
        catch {
            // Offline / no auth — keep localStorage values.
        }
        finally {
            this.syncInitialized = true;
        }
    }
    resetForLogout() {
        this.syncInitialized = false;
        if (this.syncTimer) {
            clearTimeout(this.syncTimer);
            this.syncTimer = null;
        }
        this.settings = { ...DEFAULT_MULTI_SCREEN_SETTINGS };
        this.saveSettingsToLocalStorage();
        this.notifyListeners();
    }
    // ============ Browser Support ============
    isSupported() {
        return typeof window !== 'undefined' && 'getScreenDetails' in window;
    }
    getBrowserSupportMessage() {
        if (this.isSupported())
            return null;
        if (typeof navigator === 'undefined') {
            return i18n.t('multiScreen.unsupported.noBrowser');
        }
        // Every iPhone/iPad browser is WebKit and single-window, so "use Chrome"
        // (which iOS Chrome cannot honour either) would be wrong advice there.
        if (isAppleTouchDevice()) {
            return i18n.t('multiScreen.unsupported.appleTouch');
        }
        const ua = navigator.userAgent;
        if (ua.includes('Firefox')) {
            return i18n.t('multiScreen.unsupported.firefox');
        }
        if (ua.includes('Safari') && !ua.includes('Chrome')) {
            return i18n.t('multiScreen.unsupported.safari');
        }
        return i18n.t('multiScreen.unsupported.other');
    }
    // ============ Permission & Detection ============
    async requestPermission() {
        if (!this.isSupported())
            return false;
        try {
            this.screenDetails = await window.getScreenDetails();
            // Listen for screen changes
            this.screenDetails.addEventListener('screenschange', () => {
                this.notifyListeners();
            });
            this.settings.hasPermission = true;
            this.settings.enabled = true;
            this.saveSettings();
            this.notifyListeners();
            return true;
        }
        catch (e) {
            console.warn('[MultiScreen] Permission denied:', e);
            this.settings.hasPermission = false;
            this.saveSettings();
            return false;
        }
    }
    hasPermission() {
        return this.settings.hasPermission && this.screenDetails !== null;
    }
    getScreens() {
        return this.screenDetails?.screens || [];
    }
    getScreenCount() {
        return this.getScreens().length || 1;
    }
    /**
     * Whether multi-monitor is configured (permission + at least one viewer screen).
     * (doesn't require live screen details)
     */
    isMultiMonitorConfigured() {
        const settings = this.getSettings();
        if (!settings.enabled)
            return false;
        return (settings.hasPermission &&
            (settings.monitorConfigs.some((c) => c.roles.viewer || c.roles.primaryViewer) ||
                settings.viewerScreenIndex >= 0));
    }
    // ============ Reporting Monitor ============
    /**
     * "Open report" entry point — assembles the cockpit with the report as the
     * primary leg (it wins the single guaranteed popup), then viewer + worklist.
     * Thin wrapper over openCockpit; see it for the warm/cold + popup-block
     * semantics. Returns { handled, blockedLeg } for the caller's fallback +
     * guidance.
     */
    async openReportForStudy(studyId, caseId) {
        return this.openCockpit({ studyId, caseId, primary: 'report' });
    }
    getReportingScreenIndex() {
        const reporting = this.settings.monitorConfigs.find((c) => c.roles.reporting);
        return reporting ? reporting.screenIndex : null;
    }
    hasReportingScreen() {
        return this.settings.monitorConfigs.some((c) => c.roles.reporting);
    }
    hasWorklistScreen() {
        return this.settings.monitorConfigs.some((c) => c.roles.worklist);
    }
    getWorklistScreenIndex() {
        const worklist = this.settings.monitorConfigs.find((c) => c.roles.worklist);
        return worklist ? worklist.screenIndex : null;
    }
    getPrimaryScreenIndex() {
        const screens = this.getScreens();
        return screens.findIndex((s) => s.isPrimary) || 0;
    }
    // ============ Window Management ============
    /**
     * Open a window sized to a target screen. Returns { window, blocked } —
     * `blocked` is true when the browser's popup blocker refused the window
     * (window.open returned null), the one reliable block signal. NOTE: the
     * browser allows only ONE popup per user gesture unless the site is
     * allow-listed for popups, so callers must open at most one fresh window per
     * click (openCockpit enforces this). Fullscreen is NOT requested here — the
     * `fullscreen` window feature is ignored by current Chrome; real fullscreen
     * is driven from inside each window via useCockpitFullscreen.
     */
    async openOnScreen(url, screenIndex, options) {
        const screens = this.getScreens();
        const target = screens[screenIndex];
        // Make URL absolute to ensure it works in new windows
        const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        // Fallback if screen not found
        if (!target) {
            console.warn(`[MultiScreen] Screen ${screenIndex} not found, opening normally`);
            const w = window.open(absoluteUrl, options?.windowName || '_blank');
            return { window: w, blocked: w === null };
        }
        const x = target.availLeft ?? target.left;
        const y = target.availTop ?? target.top;
        const w = target.availWidth;
        const h = target.availHeight;
        const features = [`left=${x}`, `top=${y}`, `width=${w}`, `height=${h}`].join(',');
        const windowName = options?.windowName || `newlumen-${Date.now()}`;
        const newWindow = window.open(absoluteUrl, windowName, features);
        if (newWindow) {
            try {
                newWindow.moveTo(x, y);
                newWindow.resizeTo(w, h);
                newWindow.focus();
            }
            catch {
                // Cross-origin or closed; ignore
            }
        }
        return { window: newWindow, blocked: newWindow === null };
    }
    /**
     * THE study/report-open entry point for multi-monitor mode. Assembles (or
     * updates) the reading cockpit while respecting the browser's hard limit of
     * one popup per user gesture:
     *
     *  - WARM (window already alive): navigate it IN PLACE over the channel — a
     *    route change, not a reload, and ZERO popups. Every live leg is
     *    navigated, so opening a study updates viewer AND report together.
     *  - COLD (window missing): open ONE fresh window. If the browser blocks it
     *    (popup blocker, when the site isn't allow-listed), stop, report which
     *    leg was blocked, and let the caller guide the user to allow popups +
     *    retry. Once the site is allow-listed, every missing leg opens in the
     *    same gesture (window.open no longer returns null), so it's one click.
     *
     * The current tab keeps its own role (a worklist tab is the worklist — it's
     * never cloned into a popup). `primary` decides which leg opens first so it
     * wins the single guaranteed popup.
     *
     * Returns { handled, blockedLeg }: handled=false means multi-screen is off
     * (caller navigates in the same window); blockedLeg names the first leg the
     * popup blocker refused (caller surfaces the allow-popups guidance).
     */
    async openCockpit(context) {
        if (this.settings.hasPermission && !this.screenDetails) {
            await this.tryReacquirePermission();
        }
        if (!this.isViewerMultiScreenEnabled()) {
            return { handled: false, blockedLeg: null };
        }
        const legs = this.planLegs(context.primary);
        let blockedLeg = null;
        let freshOpens = 0;
        for (const leg of legs) {
            // The current tab already plays this role — don't clone it into a popup.
            if (this.currentTabPlaysRole(leg))
                continue;
            const result = await this.routeLeg(leg, context);
            if (result === 'navigated')
                continue;
            if (result === 'blocked') {
                // One popup per gesture: the first block means the rest would block
                // too. Stop and let the caller guide the user to allow popups.
                blockedLeg = leg;
                break;
            }
            if (result === 'opened')
                freshOpens += 1;
        }
        // Learn the popup posture: a clean multi-window open means the site is
        // allow-listed; a block means it isn't (drives one-click vs guided retry).
        if (blockedLeg)
            this.setPopupsAllowed(false);
        else if (freshOpens >= 2)
            this.setPopupsAllowed(true);
        return { handled: true, blockedLeg };
    }
    /** Which cockpit legs to assemble, primary first, for the configured roles. */
    planLegs(primary) {
        const legs = [];
        if (this.hasViewerScreen())
            legs.push('viewer');
        if (this.hasReportingScreen())
            legs.push('report');
        if (this.hasWorklistScreen())
            legs.push('worklist');
        // Primary leg first so it wins the single guaranteed popup.
        return legs.sort((a, b) => (a === primary ? -1 : b === primary ? 1 : 0));
    }
    /** True when THIS window/tab is already serving the given cockpit role. */
    currentTabPlaysRole(leg) {
        const path = window.location.pathname;
        if (leg === 'viewer')
            return path.startsWith('/viewer/');
        if (leg === 'report')
            return path.startsWith('/report/') || path.startsWith('/teleconsultation/report/');
        // worklist
        return path === '/' || path.startsWith('/study/list') || path.startsWith('/teleconsultation');
    }
    /**
     * Route one leg: navigate a live window over the channel (no popup), or
     * open one fresh window. Returns 'navigated' | 'opened' | 'blocked'.
     */
    async routeLeg(leg, context) {
        const { studyId, caseId, seriesId } = context;
        if (leg === 'viewer') {
            if (await discoverWindow('viewer')) {
                windowChannel.loadStudy(studyId, { seriesId, caseId });
                this.focusHandle(this.viewerWindowHandle);
                return 'navigated';
            }
            const params = new URLSearchParams();
            if (seriesId)
                params.set('series', seriesId);
            if (caseId)
                params.set('caseId', caseId);
            const qs = params.toString();
            const url = `/viewer/${encodeURIComponent(studyId)}${qs ? `?${qs}` : ''}`;
            const r = await this.openOnScreen(url, this.getViewerScreenIndex(), {
                windowName: PRIMARY_VIEWER_WINDOW_NAME,
            });
            this.viewerWindowHandle = r.window;
            return r.blocked ? 'blocked' : 'opened';
        }
        if (leg === 'report') {
            if (await discoverWindow('report')) {
                windowChannel.loadReport(studyId, caseId);
                this.focusHandle(this.reportWindowHandle);
                return 'navigated';
            }
            const url = caseId
                ? `/teleconsultation/report/${caseId}`
                : `/report/${encodeURIComponent(studyId)}`;
            const idx = this.getReportingScreenIndex();
            if (idx === null)
                return 'navigated'; // no reporting screen — nothing to do
            const r = await this.openOnScreen(url, idx, { windowName: 'newlumen-report' });
            this.reportWindowHandle = r.window;
            return r.blocked ? 'blocked' : 'opened';
        }
        // worklist
        if (this.worklistWindowHandle && !this.worklistWindowHandle.closed)
            return 'navigated';
        if (await discoverWindow('worklist'))
            return 'navigated';
        const worklistIndex = this.getWorklistScreenIndex();
        if (worklistIndex === null)
            return 'navigated';
        const worklistUrl = caseId ? '/teleconsultation' : '/study/list';
        const r = await this.openOnScreen(worklistUrl, worklistIndex, {
            windowName: 'newlumen-worklist',
        });
        this.worklistWindowHandle = r.window;
        return r.blocked ? 'blocked' : 'opened';
    }
    /** Persist whether the site is allow-listed for popups (device-local). */
    setPopupsAllowed(allowed) {
        if (this.settings.popupsAllowed === allowed)
            return;
        this.updateSettings({ popupsAllowed: allowed });
    }
    /** Best-effort focus of a popout we hold a handle to. */
    focusHandle(handle) {
        try {
            if (handle && !handle.closed)
                handle.focus();
        }
        catch {
            // Cross-origin or closed; ignore.
        }
    }
    /**
     * Every screen carrying the viewer role, primary first, then screenIndex
     * order — the ordering that maps multi-monitor hanging-protocol spans onto
     * monitors (span 0 = primary viewer window, span N = Nth extra screen).
     */
    getViewerScreenIndices() {
        const primary = this.getViewerScreenIndex();
        const rest = this.settings.monitorConfigs
            .filter((c) => c.roles.viewer || c.roles.primaryViewer)
            .map((c) => c.screenIndex)
            .filter((idx) => idx !== primary)
            .sort((a, b) => a - b);
        return [primary, ...rest];
    }
    /**
     * Fan the viewer out for a multi-monitor hanging protocol: make sure viewer
     * windows 2..spanCount exist and show `studyId`. Called by the PRIMARY viewer
     * window when a protocol with monitor spans matches. Warm windows are
     * navigated in place (addressed by window name so the rest of the fleet
     * doesn't also jump); cold ones open on their span's screen. Runs outside a
     * user gesture, so cold opens can hit the popup blocker on non-allowlisted
     * setups — blocked spans are reported for the caller's retry toast (the
     * cockpit's existing popup UX; the retry click IS a gesture).
     */
    async ensureViewerSpans(studyId, spanCount, opts) {
        const blockedSpans = [];
        const screenIndices = this.getViewerScreenIndices();
        for (let span = 1; span < spanCount; span++) {
            const windowName = viewerWindowNameForSpan(span);
            if (await discoverWindow('viewer', 300, windowName)) {
                windowChannel.loadStudy(studyId, { caseId: opts?.caseId, targetWindowName: windowName });
                continue;
            }
            const screenIndex = screenIndices[span];
            if (screenIndex === undefined)
                continue; // fewer live screens than spans
            // Carry the case scope the warm path already sends, so a cold-opened span
            // window hangs the same case-filtered series as the primary viewer.
            const qs = opts?.caseId ? `?caseId=${encodeURIComponent(opts.caseId)}` : '';
            const url = `/viewer/${encodeURIComponent(studyId)}${qs}`;
            const r = await this.openOnScreen(url, screenIndex, { windowName });
            if (r.blocked) {
                blockedSpans.push(span);
                // One popup per gesture: the rest would block too, and the posture is
                // learned exactly like openCockpit's.
                this.setPopupsAllowed(false);
                break;
            }
        }
        return { blockedSpans };
    }
    /**
     * Get the screen index for the viewer based on monitor roles.
     * Priority: primaryViewer > viewer > legacy viewerScreenIndex
     */
    getViewerScreenIndex() {
        // First, check monitorConfigs for primaryViewer role
        const primaryViewer = this.settings.monitorConfigs.find((c) => c.roles.primaryViewer);
        if (primaryViewer) {
            return primaryViewer.screenIndex;
        }
        // Then check for viewer role
        const viewer = this.settings.monitorConfigs.find((c) => c.roles.viewer);
        if (viewer) {
            return viewer.screenIndex;
        }
        // Fall back to legacy setting
        return this.settings.viewerScreenIndex;
    }
    /**
     * Check if a viewer screen is configured
     */
    hasViewerScreen() {
        const hasRoleConfig = this.settings.monitorConfigs.some((c) => c.roles.viewer || c.roles.primaryViewer);
        return hasRoleConfig || this.settings.viewerScreenIndex >= 0;
    }
    /**
     * Check if multi-screen viewing is fully enabled and configured.
     * Respect the user's "Multi-Monitor Mode" toggle — roles/permission alone must not open new windows.
     */
    isViewerMultiScreenEnabled() {
        if (!this.settings.enabled) {
            return false;
        }
        // The toggle syncs across devices through the user's preferences, so it can
        // be on while this browser has no Window Management API (iPad, iPhone,
        // Firefox). A single-window platform never assembles a cockpit of popups:
        // the caller opens the study in this tab instead.
        if (!this.isSupported()) {
            return false;
        }
        const hasPermission = this.hasPermission();
        const hasStoredPermission = this.settings.hasPermission;
        const hasViewerRole = this.settings.monitorConfigs.some((c) => c.roles.viewer || c.roles.primaryViewer);
        if (!hasViewerRole) {
            return this.settings.viewerScreenIndex >= 0 && (hasPermission || hasStoredPermission);
        }
        return hasPermission || hasStoredPermission;
    }
    // ============ Settings ============
    getSettings() {
        return { ...this.settings };
    }
    updateSettings(updates) {
        this.settings = { ...this.settings, ...updates };
        this.saveSettings();
        this.notifyListeners();
    }
    setViewerScreen(screenIndex) {
        this.updateSettings({ viewerScreenIndex: screenIndex });
    }
    setEnabled(enabled) {
        this.updateSettings({ enabled });
    }
    // ============ Monitor Configuration ============
    getMonitorConfig(screenIndex) {
        const existing = this.settings.monitorConfigs.find((c) => c.screenIndex === screenIndex);
        if (existing)
            return existing;
        // Return default config
        return {
            screenIndex,
            roles: { ...DEFAULT_MONITOR_ROLES },
            defaultLayout: 'default',
        };
    }
    updateMonitorConfig(screenIndex, updates) {
        const configs = [...this.settings.monitorConfigs];
        const existingIdx = configs.findIndex((c) => c.screenIndex === screenIndex);
        if (existingIdx >= 0) {
            configs[existingIdx] = {
                ...configs[existingIdx],
                ...updates,
                roles: updates.roles
                    ? { ...configs[existingIdx].roles, ...updates.roles }
                    : configs[existingIdx].roles,
            };
        }
        else {
            configs.push({
                screenIndex,
                roles: updates.roles
                    ? { ...DEFAULT_MONITOR_ROLES, ...updates.roles }
                    : { ...DEFAULT_MONITOR_ROLES },
                defaultLayout: updates.defaultLayout || 'default',
                label: updates.label,
            });
        }
        this.updateSettings({ monitorConfigs: configs });
    }
    updateMonitorRole(screenIndex, role, enabled) {
        const config = this.getMonitorConfig(screenIndex);
        const newRoles = { ...config.roles, [role]: enabled };
        // If setting primaryViewer, also enable viewer
        if (role === 'primaryViewer' && enabled) {
            newRoles.viewer = true;
        }
        // If disabling viewer, also disable primaryViewer
        if (role === 'viewer' && !enabled) {
            newRoles.primaryViewer = false;
        }
        this.updateMonitorConfig(screenIndex, { roles: newRoles });
    }
    updateMonitorLayout(screenIndex, layout) {
        this.updateMonitorConfig(screenIndex, { defaultLayout: layout });
    }
    resetMonitorConfig(screenIndex) {
        const configs = this.settings.monitorConfigs.filter((c) => c.screenIndex !== screenIndex);
        this.updateSettings({ monitorConfigs: configs });
    }
    /**
     * Flash indicators on all screens to help user identify which is which
     */
    async identifyMonitors() {
        const screens = this.getScreens();
        if (screens.length === 0) {
            console.warn('[MultiScreen] No screens detected for identification');
            return;
        }
        // Open windows sequentially to avoid popup blocker issues
        for (let i = 0; i < screens.length; i++) {
            const screen = screens[i];
            // Calculate center position on the screen
            const width = 300;
            const height = 200;
            const left = screen.left + Math.floor((screen.availWidth - width) / 2);
            const top = screen.top + Math.floor((screen.availHeight - height) / 2);
            // Create an HTML content as a data URL
            /* eslint-disable no-restricted-syntax -- a chromeless 300x200 popup on a
               desktop monitor: no browser toolbars, so 100vh is the visible height. */
            const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Monitor ${i + 1}</title>
          <style>
            body {
              margin: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
              color: white;
              font-family: system-ui, -apple-system, sans-serif;
            }
            .number {
              font-size: 100px;
              font-weight: bold;
              text-shadow: 0 4px 20px rgba(0,0,0,0.5);
            }
            .label {
              font-size: 18px;
              opacity: 0.7;
              margin-top: 8px;
            }
            .resolution {
              font-size: 14px;
              opacity: 0.5;
              margin-top: 4px;
            }
          </style>
        </head>
        <body>
          <div class="number">${i + 1}</div>
          <div class="label">Monitor ${i + 1}${screen.isPrimary ? ' (Primary)' : ''}</div>
          <div class="resolution">${screen.width} x ${screen.height}</div>
          <script>setTimeout(() => window.close(), 3000);</script>
        </body>
        </html>
      `;
            /* eslint-enable no-restricted-syntax */
            const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(html);
            const popup = window.open(dataUrl, `identify-monitor-${i}`, `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`);
            if (!popup) {
                console.warn(`[MultiScreen] Popup blocked for monitor ${i + 1}. Please allow popups for this site.`);
            }
        }
    }
    loadSettings() {
        try {
            if (typeof localStorage === 'undefined') {
                return { ...DEFAULT_MULTI_SCREEN_SETTINGS };
            }
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                return { ...DEFAULT_MULTI_SCREEN_SETTINGS, ...JSON.parse(stored) };
            }
        }
        catch (e) {
            console.error('[MultiScreen] Failed to load settings:', e);
        }
        return { ...DEFAULT_MULTI_SCREEN_SETTINGS };
    }
    saveSettings() {
        this.saveSettingsToLocalStorage();
        this.scheduleSyncToBackend();
    }
    saveSettingsToLocalStorage() {
        try {
            if (typeof localStorage === 'undefined')
                return;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
        }
        catch (e) {
            console.error('[MultiScreen] Failed to save settings:', e);
        }
    }
    scheduleSyncToBackend() {
        if (!this.syncInitialized)
            return;
        if (this.syncTimer)
            clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => {
            this.syncToBackendNow();
        }, SYNC_DEBOUNCE_MS);
    }
    syncToBackendNow() {
        putUserPreferences({
            ...(this.prefsBase ?? {}),
            multiScreen: {
                enabled: this.settings.enabled,
                openFullscreen: this.settings.openFullscreen,
            },
        }).catch((error) => {
            console.warn('[MultiScreen] Failed to sync to backend:', error);
        });
    }
    // ============ Subscriptions ============
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notifyListeners() {
        this.listeners.forEach((l) => l());
    }
    // ============ Re-initialize ============
    /**
     * Try to re-acquire screen details if we previously had permission.
     * Useful on page load when we have settings but need fresh screen data.
     */
    async tryReacquirePermission() {
        if (!this.isSupported() || !this.settings.hasPermission) {
            return false;
        }
        try {
            this.screenDetails = await window.getScreenDetails();
            this.screenDetails.addEventListener('screenschange', () => {
                this.notifyListeners();
            });
            this.notifyListeners();
            return true;
        }
        catch {
            // Permission may have been revoked
            this.settings.hasPermission = false;
            this.saveSettings();
            return false;
        }
    }
}
export const multiScreenService = new MultiScreenService();
